import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	forwardRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBoolean } from '@app-shared/decorators/coerce-boolean.decorator';
import { RealStateFilterFeatures } from '@app-model/promotedContent';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { RealEstateFeaturesOptionsService } from './real-estate-features-options.service';

@Component({
	selector: 'app-real-estate-features-multi-select',
	templateUrl: './real-estate-features-multi-select.component.html',
	styleUrls: ['./real-estate-features-multi-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RealEstateFeaturesMultiSelectComponent),
			multi: true
		}
	]
})
export class RealEstateFeaturesMultiSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
	@Input() entityType!: string;
	@Input() entityId!: string;
	@Input() labelKey = 'promotedContents.realEstateFilterOptions.features';
	@Input() placeholderKey = 'promotedContents.realEstateFilterOptions.selectOption';
	@Input() selectedCountKey = 'searchProperties.filters.selectedCount';
	@Input() selectedListAriaLabelKey = 'searchProperties.filters.selectedOptions';
	@Input() removeSelectionAriaLabelKey = 'searchProperties.filters.removeSelection';
	@Input() appearance: 'fill' | 'outline' | 'standard' | 'legacy' = 'outline';
	@Input() searchDebounceMs = 250;
	@coerceBoolean() @Input() isDisabled = false;

	public disabled = false;
	public value: string[] = [];
	public options: string[] = [];
	public optionLabels: Record<string, string> = {};
	public searchTerm = '';

	private readonly destroy$ = new Subject<void>();
	private readonly searchTerm$ = new Subject<string>();
	private onChange: (value: string[]) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(
		private readonly featuresOptionsService: RealEstateFeaturesOptionsService,
		private readonly cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.searchTerm$
			.pipe(
				debounceTime(this.searchDebounceMs),
				distinctUntilChanged(),
				switchMap(searchTerm => {
					if (!this.entityType || !this.entityId) {
					return this.featuresOptionsService
						.searchFeaturesByCardholder(searchTerm, this.value)
						.pipe(catchError(() => of([] as RealStateFilterFeatures[])));
					}

					return this.featuresOptionsService
						.searchFeatures(this.entityType, this.entityId, searchTerm)
						.pipe(catchError(() => of([] as RealStateFilterFeatures[])));
				}),
				takeUntil(this.destroy$)
			)
			.subscribe(features => {
				this.assignFeatureOptions(features);
			});

		this.loadOptions();
	}

	ngOnChanges(changes: SimpleChanges): void {
		const entityTypeChanged = !!changes['entityType'] && !changes['entityType'].firstChange;
		const entityIdChanged = !!changes['entityId'] && !changes['entityId'].firstChange;

		if (entityTypeChanged || entityIdChanged) {
			this.loadOptions();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	writeValue(value: string[] | null): void {
		this.value = Array.isArray(value) ? [...value] : [];
		this.ensureSelectedLabels();
		this.fetchMissingLabels();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: string[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	onSelectionChange(value: string[]): void {
		this.value = Array.isArray(value) ? [...value] : [];
		this.ensureSelectedLabels();
		this.onChange(this.value);
		this.onTouched();
		this.cdr.markForCheck();
	}

	onSearchTermChange(searchTerm: string): void {
		this.searchTerm = searchTerm;
		this.searchTerm$.next(searchTerm);
	}

	private loadOptions(): void {
		this.searchTerm$.next(this.searchTerm);
	}

	private assignFeatureOptions(features: RealStateFilterFeatures[]): void {
		this.options = features.map(feature => feature?.slug).filter((slug): slug is string => !!slug);
		const newLabels = features.reduce<Record<string, string>>((acc, feature) => {
			if (feature?.slug) {
				acc[feature.slug] = feature.label || feature.slug;
			}
			return acc;
		}, {});
		this.value.forEach(slug => {
			if (slug && this.optionLabels[slug] && this.optionLabels[slug] !== slug) {
				newLabels[slug] = this.optionLabels[slug];
			}
		});
		this.optionLabels = newLabels;
		this.ensureSelectedLabels();
		this.fetchMissingLabels();
		this.cdr.markForCheck();
	}

	private ensureSelectedLabels(): void {
		this.value.forEach(slug => {
			if (slug && !this.optionLabels[slug]) {
				this.optionLabels[slug] = slug;
			}
		});
	}

	private fetchMissingLabels(): void {
		const missingSlugs = this.value.filter(slug => slug && this.optionLabels[slug] === slug);
		if (!missingSlugs.length) {
			return;
		}
		const source$ =
			!this.entityType || !this.entityId
				? this.featuresOptionsService.searchFeaturesByCardholder('', missingSlugs)
				: this.featuresOptionsService.searchFeatures(this.entityType, this.entityId, '');
		source$
			.pipe(
				catchError(() => of([] as RealStateFilterFeatures[])),
				takeUntil(this.destroy$)
			)
			.subscribe(features => {
				const labelsFromLoad = features.reduce<Record<string, string>>((acc, f) => {
					if (f?.slug) {
						acc[f.slug] = f.label || f.slug;
					}
					return acc;
				}, {});
				let updated = false;
				missingSlugs.forEach(slug => {
					if (labelsFromLoad[slug] && labelsFromLoad[slug] !== slug) {
						this.optionLabels = { ...this.optionLabels, [slug]: labelsFromLoad[slug] };
						updated = true;
					}
				});
				if (updated) {
					this.cdr.markForCheck();
				}
			});
	}

	get isSelectDisabled(): boolean {
		return this.disabled || this.isDisabled;
	}
}
